import { useCallback } from 'react'

import { CouponApplyResult } from 'src/Components/ScheduleSummary'
import { useAppDispatch } from 'src/Hooks/redux'
import { useOrderBudgetSync } from 'src/Hooks/useOrderBudgetSync'
import { useThresholdCoupon } from 'src/Hooks/useThresholdCoupon'
import { useThresholdCouponSummary } from 'src/Hooks/useThresholdCouponSummary'
import {
  Order,
  OrderCheckoutInfoResponse,
  OrderItemType,
  OrderStatus,
} from 'src/Redux/artistOrderApi'
import { useBudgetRange } from 'src/Redux/spotifyBudgetRangeApi'
import { setCampaignBudget } from 'src/Redux/spotifyCampaignMatchingProcess'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

type UseSpotifyThresholdCouponParams = {
  campaignId: number
  budget: number | null
  appliedCoupon: string
  isManualMatching: boolean
  isOrderFullyCovered: boolean
  isPaymentSucceeded: boolean
  currentOrder?: Order | null
  checkoutInfoData?: OrderCheckoutInfoResponse | null
  formatAmount: (value: number) => string
  onApplyPromocode: (
    couponCode: string,
  ) => Promise<CouponApplyResult | undefined>
  onRemoveCoupon: () => void
  updateOrderItem: Parameters<typeof useOrderBudgetSync>[0]['updateOrderItem']
  triggerCheckoutInfo: Parameters<
    typeof useOrderBudgetSync
  >[0]['triggerCheckoutInfo']
}

export const useSpotifyThresholdCoupon = ({
  campaignId,
  budget: rawBudget,
  appliedCoupon,
  isManualMatching,
  isOrderFullyCovered,
  isPaymentSucceeded,
  currentOrder,
  checkoutInfoData,
  formatAmount,
  onApplyPromocode,
  onRemoveCoupon,
  updateOrderItem,
  triggerCheckoutInfo,
}: UseSpotifyThresholdCouponParams): ReturnType<
  typeof useThresholdCouponSummary
> & { isSyncing: boolean } => {
  const dispatch = useAppDispatch()
  const isOrderMutable = currentOrder?.status !== OrderStatus.Checkout
  const { data: budgetRange } = useBudgetRange()
  const budget = rawBudget ?? 0

  const applyBudget = useCallback(
    (value: number): void => {
      dispatch(setCampaignBudget(value))
    },
    [dispatch],
  )

  const isSyncing = useOrderBudgetSync({
    enabled: Boolean(checkoutInfoData) && !isPaymentSucceeded,
    orderId: currentOrder?.id,
    campaignItem: currentOrder?.items.find(
      (item) =>
        item.type === OrderItemType.SpotifyCampaign &&
        item.sourceId === campaignId,
    ),
    coupons: appliedCoupon ? [appliedCoupon] : [],
    budget,
    updateOrderItem,
    triggerCheckoutInfo,
    isOrderMutable,
  })

  const threshold = useThresholdCoupon({
    checkoutInfo: checkoutInfoData,
    isManualMatching,
    appliedCoupon,
    budgetRange,
    budget,
    isOrderFullyCovered,
    isOrderMutable,
    onApplyPromocode,
    onRemoveCoupon,
    applyBudget,
  })

  const isHidden = isOrderFullyCovered
  const totalDiscount = checkoutInfoData?.breakdown.totalDiscount ?? 0

  const summary = useThresholdCouponSummary({
    threshold,
    isHidden,
    campaignId,
    totalDiscount,
    formatAmount,
    onApplyPromocode,
    platformName: CampaignPlatformTypeEnum.SPOTIFY,
  })

  return { ...summary, isSyncing }
}
